import { Wallet, WalletAddress, WalletBackendIdentifierString } from 'api/types/walletApiTypes'
import DataSource from 'constants/sources/DataSource'
import { canonical } from 'utils/Converters'

export const EMPTY_ADDRESS = 'Empty address'
export const ADDRESS_EXISTS = 'Wallet already exists'
export const INVALID_EVM_ADDRESS = 'Not a valid EVM address'
export const INVALID_SOL_ADDRESS = 'Not a valid Solana address'

/**
 * Regexes for ETH addresses borrowd from the web3-validator package,
 * which we can't use directly because it fails Snyk security checks.
 *
 * Source: https://github.com/web3/web3.js/blob/5341c3ab30aee24f678fc155d3e41483e3f5e68f/packages/web3-validator/src/validation/address.ts#L50
 */
const ETH_REGEX = /^(0x)?[0-9a-f]{40}$/i

// Just a basic check that the wallet is not null and doesn't already exist,
// then have separate chain checks.
export function validateIsNewWalletAddress(
  wallets?: Wallet[],
  address?: WalletAddress | null,
  chain?: DataSource
) {
  if (address === null || address === undefined || address.length === 0) {
    throw new Error(EMPTY_ADDRESS)
  }

  const isDuplicateChainAndAddress = wallets?.some(
    (wallet) => canonical(wallet.addr) === canonical(address) && wallet.chain === chain
  )

  if (isDuplicateChainAndAddress) {
    throw new Error(ADDRESS_EXISTS)
  }
}

export function validateEvmWalletAddress(address: WalletAddress) {
  const isValidEthAddress = ETH_REGEX.test(address)

  if (!isValidEthAddress) {
    throw new Error(INVALID_EVM_ADDRESS)
  }
}

export function getWalletNameAddrChainFromDropdownValue(dropdownValue: string) {
  // the dropdown value for wallets is in the format name@identifier@source
  return dropdownValue.split('@') as [string, string, DataSource]
}

export function getWalletAddrFromDropdownValue(dropdownValue: string) {
  return getWalletNameAddrChainFromDropdownValue(dropdownValue)[1]
}

export function getWalletSourceFromDropdownValue(dropdownValue: string) {
  return getWalletNameAddrChainFromDropdownValue(dropdownValue)[2]
}

export function getWalletAddrSourceFromDropdownValue(
  dropdownValue: string
): WalletBackendIdentifierString {
  const parts = getWalletNameAddrChainFromDropdownValue(dropdownValue)
  return `${parts[1]}@${parts[2]}`
}

export function findMatchingWallet(
  wallets: Wallet[],
  name: string,
  addr: WalletAddress,
  chain: DataSource
) {
  return wallets.find((w) => w.name === name && w.addr === addr && w.chain === chain)
}

// in the special case where we have an internal transfer between a csv import and an existing
// wallet, this function captures this txn while source filtering with either the csv import
// or the existing source
export function isInternalCsvTxnWithMatchingAddr(
  transactionSource: DataSource,
  transactionAddresses: string[],
  filterSource: DataSource,
  filterAddress: string
) {
  return (
    [transactionSource, filterSource].includes(DataSource.CsvImport) &&
    transactionAddresses.includes(filterAddress)
  )
}
